import { useState, useRef, useEffect, type ReactNode } from 'react'
import { cn } from './cn'

/**
 * Inline editable text. Click to edit; Enter commits; Esc cancels.
 * Per Atlas handoff Core Interaction #3.
 */
export function InlineText({
  value,
  onCommit,
  multiline = false,
  className,
  inputClassName,
  placeholder,
  disabled,
}: {
  value: string
  onCommit: (next: string) => void | Promise<void>
  multiline?: boolean
  className?: string
  inputClassName?: string
  placeholder?: string
  disabled?: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)

  useEffect(() => { if (!editing) setDraft(value) }, [value, editing])
  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus()
      if ('select' in ref.current) ref.current.select()
    }
  }, [editing])

  function commit() {
    if (draft !== value) onCommit(draft)
    setEditing(false)
  }
  function cancel() {
    setDraft(value)
    setEditing(false)
  }
  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { e.preventDefault(); cancel() }
    else if (e.key === 'Enter' && !multiline) { e.preventDefault(); commit() }
    else if (e.key === 'Enter' && multiline && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commit() }
  }

  if (!editing) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setEditing(true)}
        className={cn(
          'text-left rounded-md px-1.5 py-0.5 -mx-1.5 hover:bg-bg-muted hover:ring-1 hover:ring-border block w-full',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
          disabled && 'cursor-default hover:bg-transparent hover:ring-0',
          className,
        )}
      >
        {value || <span className="text-fg-disabled">{placeholder ?? 'Click to add'}</span>}
      </button>
    )
  }

  const Tag = multiline ? 'textarea' : 'input'
  return (
    <Tag
      ref={ref as never}
      value={draft}
      onChange={e => setDraft((e.target as HTMLInputElement).value)}
      onKeyDown={onKey}
      onBlur={commit}
      placeholder={placeholder}
      rows={multiline ? 4 : undefined}
      className={cn(
        'w-full rounded-md border border-accent bg-bg px-1.5 py-0.5 -mx-1.5 text-inherit',
        'focus:outline-none focus:ring-2 focus:ring-accent/30',
        multiline && 'resize-y',
        inputClassName,
      )}
    />
  )
}

/**
 * Inline select (button → opens dropdown / click-outside cancels).
 * Children render the *display* form; pass options for menu.
 */
export function InlineSelect<T extends string | number | null>({
  value,
  options,
  onCommit,
  display,
  disabled,
  className,
}: {
  value: T
  options: Array<{ value: T; label: ReactNode }>
  onCommit: (v: T) => void | Promise<void>
  display: ReactNode
  disabled?: boolean
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  return (
    <div ref={root} className={cn('relative inline-block', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        className={cn(
          'rounded-md px-1.5 py-0.5 -mx-1.5 text-left hover:bg-bg-muted hover:ring-1 hover:ring-border',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
          disabled && 'cursor-default hover:bg-transparent hover:ring-0',
        )}
      >
        {display}
      </button>
      {open && (
        <div className="absolute z-50 mt-1 min-w-[180px] rounded-lg border border-border bg-bg-elevated p-1 shadow-lg">
          {options.map((o, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { setOpen(false); if (o.value !== value) onCommit(o.value) }}
              className={cn(
                'flex w-full items-center px-2 py-1.5 text-sm rounded-md hover:bg-bg-muted text-left',
                o.value === value && 'bg-bg-muted',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Inline date input — click to reveal native date input.
 */
export function InlineDate({
  value,
  onCommit,
  placeholder = 'No date',
  disabled,
}: {
  value: string | null
  onCommit: (v: string | null) => void | Promise<void>
  placeholder?: string
  disabled?: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => { if (!editing) setDraft(value ?? '') }, [value, editing])
  useEffect(() => { if (editing) ref.current?.focus() }, [editing])

  function commit() {
    const next = draft || null
    if (next !== value) onCommit(next)
    setEditing(false)
  }

  if (!editing) {
    const display = value
      ? new Date(value + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
      : <span className="text-fg-disabled">{placeholder}</span>
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setEditing(true)}
        className="rounded-md px-1.5 py-0.5 -mx-1.5 text-left hover:bg-bg-muted hover:ring-1 hover:ring-border focus:outline-none"
      >
        {display}
      </button>
    )
  }
  return (
    <input
      ref={ref}
      type="date"
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); commit() }
        if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false) }
      }}
      className="rounded-md border border-accent bg-bg px-1.5 py-0.5 -mx-1.5 text-inherit focus:outline-none focus:ring-2 focus:ring-accent/30"
    />
  )
}
